import React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import SchoolIcon from '@mui/icons-material/School'
import { cellState, formatDate, pathwayTitle, SOURCE_LABEL } from './matrixStatus'

const describe = (cell, course, state) => {
  const lines = [state.label]
  if (cell?.pending && cell.status && cell.status !== 'notCompleted') {
    lines.push('A newer certificate is waiting to be approved')
  }
  if (cell?.completedOn) lines.push(`Completed ${formatDate(cell.completedOn)}`)
  if (cell?.expiresAt) lines.push(`Expires ${formatDate(cell.expiresAt)}`)
  const pathway = pathwayTitle(course, cell?.pathwayId)
  if (pathway) lines.push(`Pathway: ${pathway}`)
  if (cell?.source) lines.push(`Evidence: ${SOURCE_LABEL[cell.source] ?? 'Recorded'}`)
  if (cell?.inProgress) lines.push('E-learning in progress')
  if (cell?.legacyOnly) lines.push('Earlier e-learning result no longer counts')
  return lines
}

// One member of staff against one course: icon + short words, with the
// detail in a tooltip (and in the aria-label for screen readers).
const MatrixCell = ({ cell, course }) => {
  const state = cellState(cell)
  const { Icon } = state
  const lines = describe(cell, course, state)
  const caption =
    state.key === 'expiring' && cell?.expiresAt ? formatDate(cell.expiresAt, 'D MMM YY') : state.short
  const eLearning = cell?.source === 'elearning'

  return (
    <Tooltip
      arrow
      title={
        <Stack>
          {lines.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </Stack>
      }
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        role="img"
        aria-label={`${course.title}: ${lines.join('. ')}`}
        sx={{ minHeight: 44, px: 0.5, lineHeight: 1 }}
      >
        <Box position="relative" display="flex">
          <Icon sx={{ color: state.color, fontSize: 20 }} />
          {eLearning && (
            <SchoolIcon
              sx={{ position: 'absolute', right: -14, top: -4, fontSize: 12, color: 'text.secondary' }}
            />
          )}
        </Box>
        {caption && (
          <Typography variant="caption" color="text.primary" noWrap sx={{ fontSize: '0.68rem', lineHeight: 1.3 }}>
            {caption}
          </Typography>
        )}
        {cell?.inProgress && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.62rem', lineHeight: 1.2 }}>
            In progress
          </Typography>
        )}
      </Stack>
    </Tooltip>
  )
}

export default React.memo(MatrixCell)
